import React, { useState, useEffect, useRef } from 'react';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

const RECENT_CHATS_URL = 'https://car.amar.co.jp/api/dashboard/recent-chat/get-all?ticketType=ALL';
// Backend's StompEndpointRegistry registers "/ws-chat" — assuming it sits under
// the same /api/dashboard context path as every other dashboard REST call.
const WS_URL = 'https://car.amar.co.jp/api/dashboard/ws-chat';
const getHistoryUrl = (chat) =>
  chat.type === 'Inquiry'
    ? `https://car.amar.co.jp/api/dashboard/inquiry-chat/history/${chat.ticketNumber}`
    : `https://car.amar.co.jp/api/dashboard/order-ticket-chat/history-by/${chat.ticketNumber}`;

const getSendUrl = (chat, message) => {
  const encodedMsg = encodeURIComponent(message);
  return chat.type === 'Inquiry'
    ? `https://car.amar.co.jp/api/dashboard/inquiry-chat/send?ticketNumber=${chat.ticketNumber}&message=${encodedMsg}`
    : `https://car.amar.co.jp/api/dashboard/order-ticket-chat/send?orderTicketNumber=${chat.ticketNumber}&message=${encodedMsg}`;
};

const getMarkReadUrl = (chat) =>
  chat.type === 'Inquiry'
    ? `https://car.amar.co.jp/api/dashboard/inquiry-chat/mark-read/${chat.ticketNumber}`
    : `https://car.amar.co.jp/api/dashboard/order-ticket-chat/mark-read/${chat.ticketNumber}`;

const getDownloadUrl = (chat, chatId) =>
  chat.type === 'Inquiry'
    ? `https://car.amar.co.jp/api/dashboard/inquiry-chat/download/${chatId}`
    : `https://car.amar.co.jp/api/dashboard/order-ticket-chat/download/${chatId}`;

const formatMsgTime = (formattedDateTime) =>
  new Date(formattedDateTime).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });

// Shared shape-mapper — used for both history entries and the single
// chatData object returned right after sending a message.
const mapChatMessage = (m) => ({
  id: m.chatId ?? m.orderChatId,
  from: (m.senderType || '').toUpperCase().includes('ADMIN') ? 'admin' : 'customer',
  senderName: m.senderFullName,
  text: m.message,
  time: formatMsgTime(m.formattedDateTime),
  sortKey: new Date(m.formattedDateTime).getTime(),
  attachmentFileName: m.attachmentFileName,
  downloadUrl: m.downloadUrl,
  isRead: m.isRead === true,
});

// Same robust token lookup used in allTickets.js — scans known localStorage
// keys first, then falls back to scanning everything for a JWT-shaped value.
const getToken = () => {
  const knownKeys = ["adminAuthToken", "authToken", "auth_token", "token", "accessToken", "jwt", "jwtToken", "adminToken", "userToken", "access_token"];
  for (const key of knownKeys) {
    const val = localStorage.getItem(key);
    if (val && val.startsWith("ey")) return val;
  }
  for (let i = 0; i < localStorage.length; i++) {
    const val = localStorage.getItem(localStorage.key(i));
    if (val && val.startsWith("ey") && val.includes(".")) return val;
  }
  return null;
};

const getAuthHeaders = () => {
  const token = getToken();
  const headers = { 'accept': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

const initials = (name) =>
  (name || '').split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';

const formatTime = (lastMessageTime) => {
  if (!lastMessageTime) return '';
  // Backend sends "2026-07-20 13:48:00.587405" — swap the space for 'T' and
  // drop sub-second digits past ms so `new Date(...)` parses it reliably.
  const iso = lastMessageTime.replace(' ', 'T').slice(0, 23);
  const date = new Date(iso);
  if (isNaN(date.getTime())) return lastMessageTime;
  return date.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const BackArrow = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M15 19l-7-7 7-7" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AdminChat = ({ onBack, onTicketRead }) => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState('');
  const [backHover, setBackHover] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [attachedFile, setAttachedFile] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const fileInputRef = useRef(null);
  const messagesBottomRef = useRef(null);
  // When true, the next scroll-to-bottom should jump instantly instead of
  // animating — set whenever we're about to load a ticket's full history
  // (opening a chat, or switching tickets), so the admin lands straight on
  // the latest message instead of watching it scroll through everything.
  // Live incoming messages while already viewing a chat still smooth-scroll.
  const skipSmoothScrollRef = useRef(true);
  const stompClientRef = useRef(null);
  const ticketSubRef = useRef(null);
  const selectedIdRef = useRef(null);
  const chatsRef = useRef([]);
  const [wsConnected, setWsConnected] = useState(false);

  const fetchRecentChats = async (isInitialLoad) => {
    try {
      const [recentChatsRes, inquiryTicketsRes, orderTicketsRes] = await Promise.allSettled([
        fetch(RECENT_CHATS_URL, { headers: { 'accept': 'application/json' } }),
        fetch('https://car.amar.co.jp/api/dashboard/raise-ticket/get-all-tickets', { headers: getAuthHeaders() }),
        fetch('https://car.amar.co.jp/api/dashboard/order-ticket/get-all-ticket', { headers: { 'accept': 'application/json' } }),
      ]);

      if (recentChatsRes.status !== 'fulfilled' || !recentChatsRes.value.ok) {
        throw new Error('Failed to load recent chats');
      }
      const result = await recentChatsRes.value.json();

      // recent-chat/get-all's userFirstName/userLastName is actually the
      // LAST MESSAGE SENDER, not the ticket's customer (that's the "Krishna
      // Yadav" bug — Krishna is the admin who replied last). The ticket-list
      // APIs have the real customer name, same ones allTickets.js uses, so
      // build a ticketNumber → real customer name map from those instead.
      const customerByTicket = {};
      if (inquiryTicketsRes.status === 'fulfilled' && inquiryTicketsRes.value.ok) {
        const inquiryData = await inquiryTicketsRes.value.json();
        (inquiryData?.data || []).forEach(t => {
          customerByTicket[t.ticketNumber] = `${t.userFirstName || ''} ${t.userLastName || ''}`.trim();
        });
      }
      if (orderTicketsRes.status === 'fulfilled' && orderTicketsRes.value.ok) {
        const orderData = await orderTicketsRes.value.json();
        (orderData?.data || []).forEach(t => {
          customerByTicket[t.orderTicketNumber] = `${t.userFirstName || ''} ${t.userLastName || ''}`.trim();
        });
      }

      const mapped = (result?.data || []).map(c => ({
        id: c.ticketNumber,
        type: c.ticketType === 'INQUIRY' ? 'Inquiry' : 'Order',
        ticketNumber: c.ticketNumber,
        customer: customerByTicket[c.ticketNumber] || `${c.userFirstName || ''} ${c.userLastName || ''}`.trim() || 'Unknown',
        lastMessage: c.lastMessage || '',
        time: formatTime(c.lastMessageTime),
        unread: c.unreadCount || 0,
        userId: c.userId,
      }));

      setChats(mapped);
      if (isInitialLoad) setSelectedId(mapped[0]?.id || null);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching recent chats:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentChats(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedChat = chats.find(c => c.id === selectedId);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  // ── WebSocket: connect once, subscribe to the global "someone messaged"
  // topics so the LEFT LIST updates live even for tickets that aren't
  // currently open — no polling needed. ──────────────────────────────────
  useEffect(() => {
    const client = new Client({
      webSocketFactory: () => {
        const token = getToken();
        const url = token ? `${WS_URL}?token=${encodeURIComponent(token)}` : WS_URL;
        return new SockJS(url);
      },
      connectHeaders: getToken() ? { Authorization: `Bearer ${getToken()}` } : {},
      reconnectDelay: 5000,
    });

    client.onConnect = () => {
      console.log('[WS] Connected to', WS_URL);
      setWsConnected(true);

      const handleGlobalMessage = (frame) => {
        console.log('[WS] admin/superadmin-chat-notifications frame:', frame.body);
        let payload;
        try { payload = JSON.parse(frame.body); } catch { return; }
        const incoming = mapChatMessage(payload);
        const ticketNumber = payload.ticketNumber ?? payload.orderTicketNumber;
        if (!ticketNumber) return;

        // Update the left list's preview/unread regardless of which chat is open.
        setChats(prev => prev.map(c => {
          if (c.ticketNumber !== ticketNumber) return c;
          const isOpen = c.id === selectedIdRef.current;
          return {
            ...c,
            lastMessage: incoming.text,
            time: incoming.time,
            unread: isOpen ? 0 : c.unread + 1,
          };
        }));

        // If this ticket's conversation is open right now, append the message
        // too (the per-ticket subscription below also does this — guarded by
        // id so we don't double-append the same chatId).
        setMessages(prev => {
          const openChat = chatsRef.current.find(c => c.id === selectedIdRef.current);
          if (!openChat || openChat.ticketNumber !== ticketNumber) return prev;
          if (prev.some(m => m.id === incoming.id)) return prev;
          return [...prev, incoming];
        });
      };

      client.subscribe('/topic/admin-chat-notifications', handleGlobalMessage);
      client.subscribe('/topic/superadmin-chat-notifications', handleGlobalMessage);

      // Dedicated topic for the recent-chat LIST itself (same topic covers
      // both inquiry and order tickets). Update the matching row in place;
      // if the ticket isn't in our list yet (brand new conversation), just
      // refetch the whole list so it picks up the correct customer name too.
      client.subscribe('/topic/recent-chat', (frame) => {
        console.log('[WS] /topic/recent-chat frame:', frame.body);
        let payload;
        try { payload = JSON.parse(frame.body); } catch { return; }
        const ticketNumber = payload.ticketNumber ?? payload.orderTicketNumber;
        if (!ticketNumber) return;

        const existing = chatsRef.current.find(c => c.ticketNumber === ticketNumber);
        if (!existing) {
          fetchRecentChats(false);
          return;
        }

        setChats(prev => prev.map(c => {
          if (c.ticketNumber !== ticketNumber) return c;
          const isOpen = c.id === selectedIdRef.current;
          return {
            ...c,
            lastMessage: payload.lastMessage ?? c.lastMessage,
            time: payload.lastMessageTime ? formatTime(payload.lastMessageTime) : c.time,
            unread: isOpen ? 0 : (payload.unreadCount ?? c.unread + 1),
          };
        }));
      });
    };

    client.onWebSocketClose = () => setWsConnected(false);
    client.onStompError = (frame) => console.error('WebSocket STOMP error:', frame);

    client.activate();
    stompClientRef.current = client;

    return () => {
      client.deactivate();
      stompClientRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── WebSocket: subscribe to the currently-open ticket's own topic so new
  // messages appear instantly without re-fetching history. ────────────────
  useEffect(() => {
    if (ticketSubRef.current) {
      try { ticketSubRef.current.unsubscribe(); } catch { /* stale connection, e.g. dev hot-reload */ }
      ticketSubRef.current = null;
    }
    if (!selectedChat || !wsConnected || !stompClientRef.current || !stompClientRef.current.connected) return;

    ticketSubRef.current = stompClientRef.current.subscribe(
      `/topic/chat/${selectedChat.ticketNumber}`,
      (frame) => {
        console.log(`[WS] /topic/chat/${selectedChat.ticketNumber} frame:`, frame.body);
        let payload;
        try { payload = JSON.parse(frame.body); } catch { return; }

        // Backend sends two different shapes on this same topic:
        // 1) a normal chat message DTO (chatId/message/senderType/...)
        // 2) a READ_RECEIPT event ({ eventType: "READ_RECEIPT", readBySenderType, ... })
        //    fired when the OTHER side marks messages as read — this should
        //    turn our own sent messages' ticks blue, not create a message bubble.
        if (payload.eventType === 'READ_RECEIPT') {
          const readBy = (payload.readBySenderType || '').toUpperCase();
          // Only relevant when the customer (USER) read OUR messages — that's
          // what should turn the admin's own sent-message ticks blue.
          if (readBy === 'USER') {
            setMessages(prev => prev.map(m => m.from === 'admin' ? { ...m, isRead: true } : m));
          }
          return;
        }

        const incoming = mapChatMessage(payload);
        setMessages(prev => prev.some(m => m.id === incoming.id) ? prev : [...prev, incoming]);

        // This ticket's chat is open right now, so any new customer message
        // that just arrived should immediately count as read — otherwise
        // the backend's unreadCount (and therefore the bell) stays stuck at
        // 1+ even though the admin is actively looking at it.
        if (incoming.from !== 'admin') {
          fetch(getMarkReadUrl(selectedChat), { method: 'PUT', headers: getAuthHeaders() })
            .then(() => onTicketRead?.(selectedChat.ticketNumber))
            .catch(err => {
              console.error('Error auto-marking live message as read:', err);
            });
        }
      }
    );

    return () => {
      if (ticketSubRef.current) {
        try { ticketSubRef.current.unsubscribe(); } catch { /* stale connection, e.g. dev hot-reload */ }
        ticketSubRef.current = null;
      }
    };
  }, [selectedId, wsConnected]);

  useEffect(() => {
    if (messagesBottomRef.current) {
      messagesBottomRef.current.scrollIntoView({ behavior: skipSmoothScrollRef.current ? 'auto' : 'smooth' });
      skipSmoothScrollRef.current = false;
    }
  }, [messages]);

  useEffect(() => {
    if (!selectedChat) { setMessages([]); return; }

    // About to load this ticket's full history — land on the latest
    // message instantly rather than animating through the whole thread.
    skipSmoothScrollRef.current = true;

    const fetchHistory = async () => {
      setMessagesLoading(true);
      setMessagesError(null);
      try {
        const response = await fetch(getHistoryUrl(selectedChat), { headers: getAuthHeaders() });
        if (!response.ok) throw new Error('Failed to load chat history');
        const data = await response.json();

        const mapped = (Array.isArray(data) ? data : []).map(mapChatMessage);

        mapped.sort((a, b) => a.sortKey - b.sortKey);
        setMessages(mapped);
      } catch (err) {
        console.error('Error fetching chat history:', err);
        setMessagesError(err.message);
      } finally {
        setMessagesLoading(false);
      }
    };

    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Shared mark-as-read logic — called both on manual click AND by the
  // effect below (which also covers the ticket that gets AUTO-selected on
  // initial load). Guarded on unread so it's a no-op for already-read chats.
  const markChatAsRead = (chat) => {
    if (!chat || !chat.unread) return;

    // Optimistic local clear, same as allTickets.js does...
    setChats(prev => prev.map(c => c.id === chat.id ? { ...c, unread: 0 } : c));

    // ...backed by the real mark-read call.
    fetch(getMarkReadUrl(chat), { method: 'PUT', headers: getAuthHeaders() })
      .then(() => onTicketRead?.(chat.ticketNumber))
      .catch(err => {
        console.error('Error marking chat as read:', err);
      });
  };

  const handleSelect = (id) => {
    setSelectedId(id);
    setAttachedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Fires on EVERY change of the selected chat — whether selection happened
  // via a click (handleSelect above) or via the auto-select-first-chat-on-
  // load logic in fetchRecentChats. That auto-select was the actual bug:
  // the ticket opened straight into view with its messages already visible,
  // but since it never passed through handleSelect, mark-read never fired —
  // so the list badge, the top message icon, and the sidebar count all kept
  // showing the old unread count even though the admin was looking right at it.
  useEffect(() => {
    if (selectedChat && selectedChat.unread > 0) {
      markChatAsRead(selectedChat);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, selectedChat]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text && !attachedFile) return;
    if (!selectedChat || sending) return;

    setSending(true);
    setSendError(null);

    // Optimistic bubble — single tick ("sent") while the request is in flight.
    const tempId = `pending-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: tempId, from: 'admin', senderName: null, text,
      time: '', sortKey: Date.now(), attachmentFileName: attachedFile?.name || null,
      downloadUrl: null, isRead: false, pending: true,
    }]);

    try {
      // Backend expects multipart/form-data with an `attachment` part — send
      // the real picked file if there is one, else an empty Blob (same
      // fallback allTickets.js uses). Don't set Content-Type manually, the
      // browser adds the correct multipart boundary for a FormData body.
      const formData = new FormData();
      if (attachedFile) {
        formData.append('attachment', attachedFile, attachedFile.name);
      } else {
        formData.append('attachment', new Blob(), '');
      }

      const response = await fetch(getSendUrl(selectedChat, text), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });
      if (!response.ok) throw new Error('Failed to send message');
      const result = await response.json();
      if (!result?.chatData) throw new Error('Unexpected response from server');

      const sentMessage = mapChatMessage(result.chatData);
      setMessages(prev => {
        const withoutTemp = prev.filter(m => m.id !== tempId);
        // If the WebSocket broadcast for this exact message already arrived
        // (race with the REST response), don't add a second copy.
        if (withoutTemp.some(m => m.id === sentMessage.id)) return withoutTemp;
        return [...withoutTemp, sentMessage];
      });
      setChats(prev => prev.map(c =>
        c.id === selectedChat.id ? { ...c, lastMessage: sentMessage.text, time: sentMessage.time } : c
      ));
      setDraft('');
      setAttachedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      console.error('Error sending message:', err);
      setSendError(err.message);
      // Drop the optimistic bubble since it never actually sent.
      setMessages(prev => prev.filter(m => m.id !== tempId));
    } finally {
      setSending(false);
    }
  };

  const handleDownloadAttachment = async (msg) => {
    if (!selectedChat || !msg.attachmentFileName) return;
    setDownloadingId(msg.id);
    try {
      const response = await fetch(getDownloadUrl(selectedChat, msg.id), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (response.status === 404) throw new Error('No attachment found.');
      if (!response.ok) throw new Error(`Download failed: ${response.statusText}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', msg.attachmentFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      setMessagesError(`Couldn't download ${msg.attachmentFileName}: ${err.message}`);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDraftKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const totalUnread = chats.reduce((sum, c) => sum + c.unread, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', flexShrink: 0 }}>
        {onBack && (
          <button
            onClick={onBack}
            onMouseEnter={() => setBackHover(true)}
            onMouseLeave={() => setBackHover(false)}
            aria-label="Back to dashboard"
            style={{
              border: 'none', background: backHover ? '#1e293b' : '#334155',
              borderRadius: '50%', width: '42px', height: '42px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, transition: 'background 0.15s ease'
            }}
          >
            <BackArrow />
          </button>
        )}
        <h2 style={{ margin: 0, color: '#e11d2e', fontWeight: 700, fontSize: '26px' }}>Messages</h2>
        {totalUnread > 0 && (
          <span style={{
            background: '#dc2626', color: '#fff', fontSize: '12px', fontWeight: 700,
            borderRadius: '999px', padding: '4px 12px'
          }}>{totalUnread} unread</span>
        )}
      </div>

      <div style={{
        display: 'flex', flex: 1, minHeight: 0, border: '1px solid #e5e7eb', borderRadius: '14px',
        overflow: 'hidden', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
      }}>
        {/* ── Chat list ── */}
        <div style={{ width: '340px', height: '100%', borderRight: '1px solid #eee', overflowY: 'auto', flexShrink: 0, background: '#fcfcfd' }}>
          {chats.map(chat => (
            <div
              key={chat.id}
              onClick={() => handleSelect(chat.id)}
              style={{
                display: 'flex', gap: '12px', padding: '16px 18px', cursor: 'pointer',
                borderBottom: '1px solid #f0f0f0',
                background: chat.id === selectedId ? '#fff' : 'transparent',
                borderLeft: chat.id === selectedId ? '3px solid #dc2626' : '3px solid transparent',
                transition: 'background 0.15s ease',
              }}
            >
              <div style={{
                width: '40px', height: '40px', borderRadius: '50%', background: '#475569',
                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '13px', fontWeight: 700, flexShrink: 0
              }}>{initials(chat.customer)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                  <span style={{
                    fontWeight: 600, fontSize: '14px', color: '#1e1e1e',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                  }}>{chat.customer}</span>
                  <span style={{ fontSize: '11px', color: '#999', flexShrink: 0 }}>{chat.time}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px' }}>
                  <span style={{
                    fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px',
                    color: chat.type === 'Inquiry' ? '#1d4ed8' : '#7e22ce',
                    background: chat.type === 'Inquiry' ? '#eff6ff' : '#faf5ff',
                    padding: '2px 7px', borderRadius: '5px'
                  }}>{chat.type}</span>
                  <span style={{ fontSize: '11px', color: '#999' }}>{chat.ticketNumber}</span>
                </div>
                <div style={{
                  fontSize: '13px', color: '#666', marginTop: '5px',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                }}>{chat.lastMessage}</div>
              </div>
              {chat.unread > 0 && (
                <span style={{
                  alignSelf: 'center', background: '#dc2626', color: '#fff', fontSize: '11px',
                  fontWeight: 700, borderRadius: '999px', minWidth: '19px', height: '19px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px', flexShrink: 0
                }}>{chat.unread}</span>
              )}
            </div>
          ))}
        </div>

        {/* ── Conversation ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
          {loading ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
              Loading conversations…
            </div>
          ) : error ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#999', gap: '6px' }}>
              <div style={{ fontSize: '28px' }}>⚠️</div>
              <div style={{ fontWeight: 700, color: '#333' }}>Couldn't load chats</div>
              <div style={{ fontSize: '13px' }}>{error}</div>
            </div>
          ) : selectedChat ? (
            <div style={{ display: 'grid', gridTemplateRows: 'auto minmax(0, 1fr) auto', height: '100%', minHeight: 0 }}>
              <div style={{ padding: '16px 24px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '38px', height: '38px', borderRadius: '50%', background: '#475569',
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '13px', fontWeight: 700, flexShrink: 0
                }}>{initials(selectedChat.customer)}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '18px', color: '#e11d2e' }}>{selectedChat.ticketNumber}</div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                    {selectedChat.customer} · {selectedChat.type} ticket
                  </div>
                </div>
              </div>

              <div style={{
                minHeight: 0, padding: '24px', overflowY: 'auto', display: 'flex',
                flexDirection: 'column', gap: '10px', background: '#f9fafb'
              }}>
                {messagesLoading ? (
                  <div style={{ textAlign: 'center', color: '#999', fontSize: '13px' }}>Loading messages…</div>
                ) : messagesError ? (
                  <div style={{ textAlign: 'center', color: '#c00', fontSize: '13px' }}>Couldn't load messages — {messagesError}</div>
                ) : messages.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#999', fontSize: '13px' }}>No messages yet</div>
                ) : (
                  messages.map((m) => (
                    <div key={m.id} style={{
                      display: 'flex', justifyContent: m.from === 'admin' ? 'flex-end' : 'flex-start',
                      alignItems: 'flex-end', gap: '8px'
                    }}>
                      {m.from !== 'admin' && (
                        <div
                          title={m.senderName || 'Customer'}
                          style={{
                            width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0,
                            background: '#475569', color: '#fff', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', fontSize: '13px', fontWeight: 700
                          }}>{initials(m.senderName)}</div>
                      )}
                      <div style={{
                        maxWidth: '60%', padding: '10px 14px', borderRadius: '14px', fontSize: '13px', lineHeight: 1.5,
                        background: m.from === 'admin' ? '#f87171' : '#fff',
                        color: m.from === 'admin' ? '#fff' : '#222',
                        border: m.from === 'admin' ? 'none' : '1px solid #eee',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                      }}>
                        {m.senderName && (
                          <div style={{
                            fontSize: '10px', fontWeight: 700, marginBottom: '3px',
                            color: m.from === 'admin' ? 'rgba(255,255,255,0.9)' : '#888'
                          }}>{m.from === 'admin' ? `${m.senderName} (Support)` : m.senderName}</div>
                        )}
                        {m.text}
                        {m.attachmentFileName && (
                          <div style={{ marginTop: '6px' }}>
                            <button
                              onClick={() => handleDownloadAttachment(m)}
                              disabled={downloadingId === m.id}
                              style={{
                                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                color: m.from === 'admin' ? '#fff' : '#2563eb', textDecoration: 'underline',
                                fontSize: '12px', fontWeight: 500, opacity: downloadingId === m.id ? 0.7 : 1,
                              }}
                            >📎 {downloadingId === m.id ? 'Downloading…' : m.attachmentFileName}</button>
                          </div>
                        )}
                        <div style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px',
                          fontSize: '10px', marginTop: '5px',
                          color: m.from === 'admin' ? 'rgba(255,255,255,0.9)' : '#999'
                        }}>
                          <span>{m.time}</span>
                          {m.from === 'admin' && (
                            <span style={{ color: m.pending ? 'rgba(255,255,255,0.8)' : (m.isRead ? '#3b82f6' : 'rgba(255,255,255,0.8)') }}>
                              {m.pending ? '✓' : '✓✓'}
                            </span>
                          )}
                        </div>
                      </div>
                      {m.from === 'admin' && (
                        <div
                          title={`${m.senderName || 'Support'} (Support)`}
                          style={{
                            width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0,
                            background: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                          <SupportAgentIcon style={{ fontSize: '16px', color: '#fff' }} />
                        </div>
                      )}
                    </div>
                  ))
                )}
                <div ref={messagesBottomRef} />
              </div>

              <div style={{ padding: '16px 24px', borderTop: '1px solid #eee' }}>
                {sendError && (
                  <div style={{ color: '#dc2626', fontSize: '12px', marginBottom: '8px' }}>
                    Couldn't send message — {sendError}
                  </div>
                )}
                {attachedFile && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px',
                    fontSize: '12px', color: '#2e7d32', background: '#f0fdf4', padding: '6px 10px',
                    borderRadius: '8px', width: 'fit-content'
                  }}>
                    📎 {attachedFile.name}
                    <button
                      onClick={() => { setAttachedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#999', fontSize: '13px', padding: 0 }}
                    >✕</button>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={(e) => setAttachedFile(e.target.files[0] || null)}
                    style={{ display: 'none' }}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sending}
                    aria-label="Attach file"
                    style={{
                      border: '1px solid #ddd', background: '#fff', borderRadius: '10px',
                      width: '42px', height: '42px', flexShrink: 0, cursor: sending ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', color: '#666'
                    }}
                  >📎</button>
                  <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={handleDraftKeyDown}
                    placeholder="Type a message…"
                    disabled={sending}
                    style={{
                      flex: 1, padding: '11px 16px', borderRadius: '10px', border: '1px solid #ddd',
                      fontSize: '13px', background: sending ? '#f5f5f5' : '#fff', color: '#222'
                    }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={sending || (!draft.trim() && !attachedFile)}
                    style={{
                      padding: '11px 22px', borderRadius: '10px', border: 'none',
                      background: (sending || (!draft.trim() && !attachedFile)) ? '#e2e2e2' : '#dc2626',
                      color: (sending || (!draft.trim() && !attachedFile)) ? '#999' : '#fff',
                      fontWeight: 700,
                      cursor: (sending || (!draft.trim() && !attachedFile)) ? 'not-allowed' : 'pointer',
                    }}
                  >{sending ? 'Sending…' : 'Send'}</button>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
              No conversations yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminChat;